import { VertexBuffer } from "@babylonjs/core/Buffers/buffer";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { eq } from "@egr/wcf/modules/utils/math";
import { ERR_INVALID_ARGUMENT } from "@egr/wcf/modules/utils/error";
import { addElements } from "@egr/wcf/modules/utils/array";
import { PbrMaterial } from "@egr/wcf/modules/core/mdl";
import { ShadowGenerator } from "@egr/wcf/modules/core/rendering/ShadowGenerator";
import { Geometry } from '@babylonjs/core//Meshes/geometry';
export class CsgUtils {
    /** @internal */
    static extrudeWall(pWall) {
        if (pWall == null) {
            const tnewError = new Error(ERR_INVALID_ARGUMENT);
        }
        let tPolygons = new Array();
        //create side/wall polygons
        let tWallPolygons = this.getWallSidePolygons(pWall);
        if (tWallPolygons != null)
            addElements(tPolygons, tWallPolygons);
        //create top and bottom (based on CAG_toPlanePolygons())
        let tHH = pWall.height * 0.5;
        let tHT = pWall.thickness * 0.5;
        let tMaxX = pWall._localEdges[1].x;
        let tPlaneCSG = CSG.fromPolygons([new CSG.Polygon([
                new CSG.Vertex(new CSG.Vector3D(-10, tHH, -tHT - 10)),
                new CSG.Vertex(new CSG.Vector3D(tMaxX + 10, tHH, -tHT - 10)),
                new CSG.Vertex(new CSG.Vector3D(tMaxX + 10, tHH, tHT + 10)),
                new CSG.Vertex(new CSG.Vector3D(-10, tHH, tHT + 10))
            ])]);
        let tWallCSG = CSG.fromPolygons(tPolygons);
        let tIntersectCSG = tPlaneCSG.intersectSub(tWallCSG);
        tIntersectCSG.polygons = tIntersectCSG.polygons.filter((pPoly) => {
            // only keep the polygons in the xz plane:
            return (Math.abs(pPoly.plane.normal.y) > 0.99);
        });
        let tBottomCSG = tIntersectCSG.translate([0, -tHH, 0]);
        let tTopCSG = tIntersectCSG.invert().translate([0, tHH, 0]);
        addElements(tPolygons, tBottomCSG.polygons);
        addElements(tPolygons, tTopCSG.polygons);
        return (CSG.fromPolygons(tPolygons));
    }
    /** @internal */
    static extrudeFence(pFenceField, pHeight, pMaterial, pMirror = true, p2d = false) {
        let tPolygons = new Array();
        if (!p2d) {
            //create side/wall polygons
            let tFencePolygons = this.getFenceSidePolygons(pFenceField, pHeight, pMaterial, pMirror);
            if (tFencePolygons != null)
                addElements(tPolygons, tFencePolygons);
        }
        if (p2d) {
            //create top
            let tV1 = new Array();
            let tV2 = new Array();
            for (let tEdge of pFenceField) {
                tV1.push(new CSG.Vertex(new CSG.Vector3D(tEdge.x, 0, tEdge.y)));
                tV2.push(new CSG.Vertex(new CSG.Vector3D(tEdge.x, pHeight, tEdge.y)));
            }
            let tPolygonTop = new Array();
            let tShared = new CsgUtils.PolygonShared(pMaterial);
            tPolygonTop.push(new CSG.Polygon([tV1[0], tV1[1], tV1[2], tV1[3]], tShared));
            tPolygonTop.push(new CSG.Polygon([tV2[0], tV2[1], tV2[2], tV2[3]], tShared));
            addElements(tPolygons, tPolygonTop);
        }
        return (CSG.fromPolygons(tPolygons));
    }
    /** @internal */
    static extrudeRoute(pRoute) {
        if (pRoute == null) {
            const tnewError = new Error(ERR_INVALID_ARGUMENT);
        }
        let tPolygons = new Array();
        //create side/wall polygons
        let tRoutePolygons = this.getRouteSidePolygons(pRoute);
        if (tRoutePolygons != null)
            addElements(tPolygons, tRoutePolygons);
        //create top and bottom (based on CAG_toPlanePolygons())
        let tHH = pRoute.height * 0.5;
        let tHT = pRoute.thickness * 0.5;
        let tMaxX = pRoute._localEdges[1].x;
        let tPlaneCSG = CSG.fromPolygons([new CSG.Polygon([
                new CSG.Vertex(new CSG.Vector3D(-10, tHH, -tHT - 10)),
                new CSG.Vertex(new CSG.Vector3D(tMaxX + 10, tHH, -tHT - 10)),
                new CSG.Vertex(new CSG.Vector3D(tMaxX + 10, tHH, tHT + 10)),
                new CSG.Vertex(new CSG.Vector3D(-10, tHH, tHT + 10))
            ])]);
        let tRouteCSG = CSG.fromPolygons(tPolygons);
        let tIntersectCSG = tPlaneCSG.intersectSub(tRouteCSG);
        tIntersectCSG.polygons = tIntersectCSG.polygons.filter((pPoly) => {
            // only keep the polygons in the xz plane:
            return (Math.abs(pPoly.plane.normal.y) > 0.99);
        });
        let tBottomCSG = tIntersectCSG.translate([0, -tHH, 0]);
        let tTopCSG = tIntersectCSG.invert().translate([0, tHH, 0]);
        addElements(tPolygons, tBottomCSG.polygons);
        addElements(tPolygons, tTopCSG.polygons);
        return (CSG.fromPolygons(tPolygons));
    }
    /** @internal */
    static getSidePolygons(pWall) {
        if (pWall == null) {
            const tnewError = new Error(ERR_INVALID_ARGUMENT);
        }
        let tPolygons = new Array();
        //create side/wall polygons
        let tWallPolygons = this.getWallSidePolygons(pWall);
        if (tWallPolygons != null) {
            tWallPolygons.splice(2, 1);
            tWallPolygons.splice(0, 1);
            addElements(tPolygons, tWallPolygons);
        }
        return (tPolygons);
    }
    static buildMeshes(pCSG, pScene) {
        if (pCSG == null)
            return (null);
        let tMeshes = new Array();
        //let tPolygons = pCSG.reTesselated().canonicalized().toTriangles();
        let tPolygons = pCSG.toTriangles();
        //sort polygons according to shared data
        tPolygons.sort(polygonSharedSort);
        //create one mesh per material
        let tCurrentPolygons = new Array();
        let tCurrentMaterial = null;
        for (let tPolygon of tPolygons) {
            let tMaterial = tPolygon.shared == null ? null : tPolygon.shared.material;
            if (tCurrentMaterial !== tMaterial) {
                let tMesh = this.polygonsToMesh(tCurrentPolygons, tCurrentMaterial, pScene);
                if (tMesh != null) {
                    ShadowGenerator.disableMeshShadow(tMesh);
                    tMeshes.push(tMesh);
                }
                tCurrentPolygons = new Array();
                tCurrentMaterial = tMaterial;
            }
            tCurrentPolygons.push(tPolygon);
        }
        let tMeshPoly = this.polygonsToMesh(tCurrentPolygons, tCurrentMaterial, pScene);
        if (tMeshPoly != null) {
            // ShadowGenerator.disableMeshShadow(tMeshPoly);
            tMeshes.push(tMeshPoly);
        }
        return (tMeshes);
    }
    //public static cube(pPoints: Array<Vector3>, pMatManager: MaterialManager, pScene: Scene): Mesh | null
    static cube(pMatManager, pScene) {
        let tRes = null;
        let tMat1 = new PbrMaterial("", pMatManager);
        tMat1.albedoColor = Color3.Blue();
        tMat1.emissiveColor = Color3.Blue();
        tMat1.disableLighting = true;
        tMat1.backFaceCulling = false;
        tMat1.freeze();
        let tCSGPolygon = new Array();
        /*
        for (let i = 0; i < pPoints.length; i++){
            let tpV = new CSG.Vertex(new CSG.Vector3D(pPoints[i].x, pPoints[i].y, pPoints[i].z));
            tCSGPolygon.push(tpV)
        }
        */
        tCSGPolygon.push(new CSG.Vertex(new CSG.Vector3D(-1, 0, 1)));
        tCSGPolygon.push(new CSG.Vertex(new CSG.Vector3D(0.5, 0, 0.5)));
        tCSGPolygon.push(new CSG.Vertex(new CSG.Vector3D(1, 0, -1)));
        tCSGPolygon.push(new CSG.Vertex(new CSG.Vector3D(1, 0, 1)));
        let polygons = new CSG.Polygon(tCSGPolygon);
        let tCSG = CSG.fromPolygons([polygons]);
        let tMeshes = this.buildMeshes(tCSG, pScene);
        if (tMeshes == null)
            return (tRes);
        for (let tMesh of tMeshes) {
            tMesh.material = tMat1;
        }
        return (tMeshes[0]);
    }
    static testCAG(pPoints, pMatManager, pScene) {
        let tMat1 = new PbrMaterial("", pMatManager);
        tMat1.albedoColor = Color3.Blue();
        tMat1.emissiveColor = Color3.Blue();
        tMat1.disableLighting = true;
        tMat1.backFaceCulling = false;
        tMat1.freeze();
        let tPoints = new Array();
        for (let tP of pPoints) {
            tPoints.push(new CSG.Vector2D(tP.x, tP.y));
        }
        let tshape1 = CAG.fromPoints(tPoints);
        let tCSG = tshape1.extrude({
            offset: [0, 0, 0.01]
        });
        tCSG = tCSG.rotateX(90);
        let tMeshes = this.buildMeshes(tCSG, pScene);
        if (tMeshes == null)
            return (null);
        for (let tMesh of tMeshes) {
            tMesh.material = tMat1;
        }
        return (tMeshes[0]);
    }
    //// private ///////////////////////////////////////////////////////////////////////////////
    static polygonsToMesh(pPolygons, pMaterial, pScene) {
        if (pPolygons == null || pPolygons.length === 0)
            return (null);
        let tVerticesMap = new Map();
        let tVertices = new Array();
        let tNormals = new Array();
        let tTexCoords = new Array();
        let tIndices = new Array();
        let addVertex = (pVertex, pPlane, pU, pV) => {
            let tHash = pVertex.getTag() + "_" + pPlane.getTag();
            let tIndex = tVerticesMap.get(tHash);
            if (tIndex == null) {
                tIndex = tVertices.length;
                tVerticesMap.set(tHash, tIndex);
                tVertices.push(pVertex.pos);
                tNormals.push(pPlane.normal);
                tTexCoords.push(pVertex.pos.dot(pU), pVertex.pos.dot(pV));
            }
            return (tIndex);
        };
        for (let tPoly of pPolygons) {
            //compute U and V axis for texture coordinate projection
            let tU = tPoly.plane.normal.cross(CSGVector3D_Y);
            if (eq(tU.length(), 0))
                tU = tPoly.plane.normal.cross(CSGVector3D_Z);
            tU = tU.unit();
            let tV = tU.cross(tPoly.plane.normal).unit();
            tIndices.push(addVertex(tPoly.vertices[2], tPoly.plane, tU, tV), addVertex(tPoly.vertices[1], tPoly.plane, tU, tV), addVertex(tPoly.vertices[0], tPoly.plane, tU, tV));
        }
        let tMesh = new Mesh("", pScene);
        tMesh.material = pMaterial;
        let tGeom = new Geometry(Geometry.RandomId(), pScene);
        tGeom.setVerticesData(VertexBuffer.PositionKind, createFloatArray3(tVertices), false);
        tGeom.setVerticesData(VertexBuffer.NormalKind, createFloatArray3(tNormals), false);
        tGeom.setVerticesData(VertexBuffer.UVKind, createFloatArray(tTexCoords), false);
        tGeom.setIndices(createUint16Array(tIndices));
        tGeom.applyToMesh(tMesh);
        return (tMesh);
    }
    static polygonsToMesh1(pPolygons, pMaterial, pScene) {
        if (pPolygons == null || pPolygons.length === 0)
            return (null);
        let tVerticesMap = new Map();
        let tVertices = new Array();
        let tNormals = new Array();
        let tTexCoords = new Array();
        let tIndices = new Array();
        let addVertex = (pVertex, pPlane, pU, pV) => {
            let tHash = pVertex.getTag() + "_" + pPlane.getTag();
            let tIndex = tVerticesMap.get(tHash);
            if (tIndex == null) {
                tIndex = tVertices.length;
                tVerticesMap.set(tHash, tIndex);
                tVertices.push(pVertex.pos);
                tNormals.push(pPlane.normal);
                tTexCoords.push(pVertex.pos.dot(pU), pVertex.pos.dot(pV));
            }
            return (tIndex);
        };
        for (let tPoly of pPolygons) {
            //compute U and V axis for texture coordinate projection
            let tU = tPoly.plane.normal.cross(CSGVector3D_Y);
            if (eq(tU.length(), 0))
                tU = tPoly.plane.normal.cross(CSGVector3D_Z);
            tU = tU.unit();
            let tV = tU.cross(tPoly.plane.normal).unit();
            tIndices.push(addVertex(tPoly.vertices[2], tPoly.plane, tU, tV), addVertex(tPoly.vertices[1], tPoly.plane, tU, tV), addVertex(tPoly.vertices[0], tPoly.plane, tU, tV));
        }
        let tMesh = new Mesh("", pScene);
        tMesh.material = pMaterial;
        let tGeom = new Geometry(Geometry.RandomId(), pScene);
        tGeom.setVerticesData(VertexBuffer.PositionKind, createFloatArray3(tVertices), false);
        tGeom.setVerticesData(VertexBuffer.NormalKind, createFloatArray3(tNormals), false);
        tGeom.setVerticesData(VertexBuffer.UVKind, createFloatArray(tTexCoords), false);
        tGeom.setIndices(createUint16Array(tIndices));
        tGeom.applyToMesh(tMesh);
        return (tMesh);
    }
    static getWallSidePolygons(pWall, pFlipped = false) {
        if (pWall == null)
            return (null);
        let tV1 = new Array();
        let tV2 = new Array();
        // check on degenerated wall polygon!
        let t_localEdges = new Array();
        if (pWall._localEdges[0].x > pWall._localEdges[1].x) {
            t_localEdges.push(pWall._localEdges[1]);
            t_localEdges.push(pWall._localEdges[0]);
        }
        else {
            t_localEdges.push(pWall._localEdges[0]);
            t_localEdges.push(pWall._localEdges[1]);
        }
        if (pWall._localEdges[2].x > pWall._localEdges[3].x) {
            t_localEdges.push(pWall._localEdges[2]);
            t_localEdges.push(pWall._localEdges[3]);
        }
        else {
            t_localEdges.push(pWall._localEdges[3]);
            t_localEdges.push(pWall._localEdges[2]);
        }
        for (let tEdge of t_localEdges) {
            tV1.push(new CSG.Vertex(new CSG.Vector3D(tEdge.x, 0, tEdge.y)));
            tV2.push(new CSG.Vertex(new CSG.Vector3D(tEdge.x, pWall.height, tEdge.y)));
        }
        let tPolygons = new Array();
        for (let e = 0, s = tV1.length - 1; e < tV1.length; s = e++) {
            let tShared;
            //assign material
            if (s === 0)
                tShared = new CsgUtils.PolygonShared(pWall._materialA);
            else if (s === 2)
                tShared = new CsgUtils.PolygonShared(pWall._materialB);
            if (pFlipped)
                tPolygons.push(new CSG.Polygon([tV1[s], tV1[e], tV2[e], tV2[s]], tShared));
            else
                tPolygons.push(new CSG.Polygon([tV1[e], tV1[s], tV2[s], tV2[e]], tShared));
        }
        return (tPolygons);
    }
    static getFenceSidePolygons(pFenceField, pHeight, pMaterial, pMirror = true) {
        let tV1 = new Array();
        let tV2 = new Array();
        for (let tEdge of pFenceField) {
            tV1.push(new CSG.Vertex(new CSG.Vector3D(tEdge.x, 0, tEdge.y)));
            tV2.push(new CSG.Vertex(new CSG.Vector3D(tEdge.x, pHeight, tEdge.y)));
        }
        let tPolygons = new Array();
        for (let e = 0, s = tV1.length - 1; e < tV1.length; s = e++) {
            let tShared;
            tShared = new CsgUtils.PolygonShared(pMaterial);
            if (s === 0)
                if (pMirror)
                    tPolygons.push(new CSG.Polygon([tV1[s], tV1[e], tV2[e], tV2[s]], tShared));
                else
                    tPolygons.push(new CSG.Polygon([tV1[e], tV1[s], tV2[s], tV2[e]], tShared));
            else if (s === 2)
                tPolygons.push(new CSG.Polygon([tV1[e], tV1[s], tV2[s], tV2[e]], tShared));
        }
        return (tPolygons);
    }
    static getRouteSidePolygons(pRoute, pFlipped = false) {
        if (pRoute == null)
            return (null);
        let tV1 = new Array();
        let tV2 = new Array();
        for (let tEdge of pRoute._localEdges) {
            tV1.push(new CSG.Vertex(new CSG.Vector3D(tEdge.x, 0, tEdge.y)));
            tV2.push(new CSG.Vertex(new CSG.Vector3D(tEdge.x, pRoute.height, tEdge.y)));
        }
        let tPolygons = new Array();
        for (let e = 0, s = tV1.length - 1; e < tV1.length; s = e++) {
            let tShared;
            //assign material
            if (s === 0)
                tShared = new CsgUtils.PolygonShared(pRoute._material);
            else if (s === 2)
                tShared = new CsgUtils.PolygonShared(pRoute._material);
            if (pFlipped)
                tPolygons.push(new CSG.Polygon([tV1[s], tV1[e], tV2[e], tV2[s]], tShared));
            else
                tPolygons.push(new CSG.Polygon([tV1[e], tV1[s], tV2[s], tV2[e]], tShared));
        }
        return (tPolygons);
    }
}
(function (CsgUtils) {
    class PolygonShared extends CSG.Polygon.Shared {
        constructor(pMaterial) {
            super(null);
            this.material = pMaterial;
        }
        getHash() {
            if (this.material != null) {
                return (this.material.id);
            }
            return (super.getHash());
        }
    }
    CsgUtils.PolygonShared = PolygonShared;
})(CsgUtils || (CsgUtils = {}));
//// helper functions //////////////////////////////////////////////////////////////////////////
var CSGVector3D_Y = new CSG.Vector3D(0, 1, 0);
var CSGVector3D_Z = new CSG.Vector3D(0, 0, 1);
function createFloatArray(pSource) {
    let tArray = new Float32Array(pSource.length);
    for (let i = 0; i < pSource.length; ++i) {
        tArray[i] = pSource[i];
    }
    return (tArray);
}
function createFloatArray3(pSource) {
    let tArray = new Float32Array(pSource.length * 3);
    let i = 0;
    for (let tV of pSource) {
        tArray[i++] = tV.x;
        tArray[i++] = tV.y;
        tArray[i++] = tV.z;
    }
    return (tArray);
}
function createUint16Array(pSource) {
    //TODO: Uint16Array.from(pSource) doesn't work on ios/safari, try again after core js update
    let tArray = new Uint16Array(pSource.length);
    for (let i = 0; i < pSource.length; ++i) {
        tArray[i] = pSource[i];
    }
    return (tArray);
}
function polygonSharedSort(a, b) {
    let tHashA = a.shared == null ? "null" : a.shared.getHash();
    let tHashB = b.shared == null ? "null" : b.shared.getHash();
    if (tHashA < tHashB) {
        return (-1);
    }
    if (tHashA > tHashB) {
        return (1);
    }
    return (0);
}
